"use client";

import type { ReactNode } from "react";
import { EditableValue } from "../forms/EditableValue";
import { ImpactChip, type ImpactChipRef } from "./ImpactChip";
import { OrgDataRow } from "./OrgDataRow";
import type { Provenance } from "./ProvenancePill";

/* -------------------------------------------------------------------------- */
/* IdentityRow                                                                 */
/* -------------------------------------------------------------------------- */

export interface IdentityRowProps {
  /** Row label (e.g. "Organization Name"). */
  label: string;
  /** Value slot — any renderable content. */
  children: ReactNode;
}

/**
 * Simple label / value row for identity & settings fields.
 * No provenance pill, no description.
 */
export function IdentityRow({ label, children }: IdentityRowProps) {
  return <OrgDataRow label={label}>{children}</OrgDataRow>;
}

/* -------------------------------------------------------------------------- */
/* FoundationalRow                                                             */
/* -------------------------------------------------------------------------- */

export interface FoundationalRowProps {
  /** Field label (e.g. "Total Physicians"). */
  label: string;
  /** Opportunities that reference this field — shown in an `ImpactChip`. */
  impactRefs?: ImpactChipRef[];
  /** Data provenance of the current value. */
  provenance: Provenance;
  /** Display value string (formatting done by the caller). */
  value: string;
  /** Called with the trimmed new string when the user commits an edit. */
  onSave: (next: string) => void;
}

/**
 * Foundational-input row: label + optional impact chip on the left,
 * `EditableValue` + `ProvenancePill` on the right.
 */
export function FoundationalRow({
  label,
  impactRefs = [],
  provenance,
  value,
  onSave,
}: FoundationalRowProps) {
  return (
    <OrgDataRow
      label={
        <>
          {label}
          <ImpactChip
            refs={impactRefs}
            provided={provenance === "user-provided"}
          />
        </>
      }
      provenance={provenance}
    >
      <EditableValue value={value} align="right" onSave={onSave} />
    </OrgDataRow>
  );
}

/* -------------------------------------------------------------------------- */
/* EncounterRow                                                                */
/* -------------------------------------------------------------------------- */

export interface EncounterRowProps {
  /** Care-setting label (e.g. "Inpatient"). */
  label: string;
  /** Annual encounter volume (rendered with `toLocaleString()`). */
  volume: number;
  /**
   * Share of total encounters as a fraction (0–1).
   * Rendered as `"· X.X%"`.
   */
  shareFraction: number;
  /** Data provenance of the volume. */
  provenance: Provenance;
  /** Called with the trimmed new string when the user commits a volume edit. */
  onSaveVolume: (next: string) => void;
}

/**
 * Encounter-segment row: label on the left, editable volume + share % +
 * `ProvenancePill` on the right.
 */
export function EncounterRow({
  label,
  volume,
  shareFraction,
  provenance,
  onSaveVolume,
}: EncounterRowProps) {
  const sharePct = (shareFraction * 100).toFixed(1);
  return (
    <OrgDataRow label={label} provenance={provenance}>
      <EditableValue
        value={volume.toLocaleString()}
        align="right"
        type="number"
        onSave={onSaveVolume}
      />
      <span className="text-(--fg-muted) text-xs ml-1">· {sharePct}%</span>
    </OrgDataRow>
  );
}

/* -------------------------------------------------------------------------- */
/* BaselineRow                                                                 */
/* -------------------------------------------------------------------------- */

export interface BaselineRowProps {
  /** Metric label (e.g. "Coding Accuracy Rate"). */
  label: string;
  /** One-line description of what this metric drives. */
  description: string;
  /** Opportunities that reference this baseline. */
  impactRefs?: ImpactChipRef[];
  /** Data provenance of the current value. */
  provenance: Provenance;
  /** Display value string. */
  value: string;
  /** Called with the trimmed new string on commit. */
  onSave: (next: string) => void;
}

/**
 * Operational-baseline row: stacked label + description on the left,
 * `EditableValue` + `ProvenancePill` on the right.
 */
export function BaselineRow({
  label,
  description,
  impactRefs = [],
  provenance,
  value,
  onSave,
}: BaselineRowProps) {
  return (
    <OrgDataRow
      label={
        <>
          {label}
          <ImpactChip
            refs={impactRefs}
            provided={provenance === "user-provided"}
          />
        </>
      }
      description={description}
      provenance={provenance}
    >
      <EditableValue value={value} align="right" onSave={onSave} />
    </OrgDataRow>
  );
}
